/* Veldskoen(R) In-Store Experience - shared engine
   Boots exactly once, queries across the whole document, re-scans on Shopify editor events,
   and tolerates any section being absent. Nothing here assumes a neighbour exists.
   Motion is progressive: reduced motion and pointer type are read live on every pass, never latched at load,
   and every effect leaves the page complete if it cannot run. */
(function () {
  'use strict';
  if (window.__instoreBooted) return;
  window.__instoreBooted = true;

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('ist-js');

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqCoarse = window.matchMedia('(pointer: coarse)');

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function now() { return (window.performance && window.performance.now) ? window.performance.now() : Date.now(); }
  function pageY() { return window.pageYOffset || root.scrollTop || 0; }

  /* ---------- Header height: the theme may stack several sticky bars (announcement + header).
       Each bar's resting place is its CSS top plus its height, wherever the page happens to be scrolled,
       so the measurement is right at load and does not depend on the bars already being stuck. ---------- */
  function measureHeader() {
    var h = 0;
    var vw = root.clientWidth || window.innerWidth;
    var candidates = doc.querySelectorAll('header, aside, .shopify-section-header, .shopify-section-group-header-group, [class*="header"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.closest && el.closest('.instore')) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      var top = parseFloat(cs.top);
      if (isNaN(top) || top < 0 || top > 300) continue; /* top: auto, or pinned somewhere other than the top edge */
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.height >= 400 || r.width < vw * 0.6) continue;
      if (top + r.height > h) h = top + r.height;
    }
    root.style.setProperty('--ist-header-h', Math.round(h) + 'px');
    /* A theme that already reserves its sticky height for anchor jumps (scroll-padding-top on the root) must not be allowed for twice */
    var reserved = parseFloat(window.getComputedStyle(root).scrollPaddingTop) || 0;
    root.style.setProperty('--ist-anchor', Math.max(0, Math.round(h - reserved)) + 'px');
  }

  /* ---------- Hero fit: a hero that opens the page ends at its chosen share of the screen,
       however many bars the theme puts above it ---------- */
  function measureHeroes() {
    var heroes = doc.querySelectorAll('.instore-hero');
    for (var i = 0; i < heroes.length; i++) {
      var hero = heroes[i];
      var top = hero.getBoundingClientRect().top + pageY();
      if (hero.getAttribute('data-fit') !== 'true' || top < 0 || top > window.innerHeight * 0.6) {
        hero.style.removeProperty('--ist-hero-top');
        continue;
      }
      hero.style.setProperty('--ist-hero-top', Math.round(top) + 'px');
    }
  }

  /* ---------- Chapter numbering: derived from the sections actually present, in document order ---------- */
  function numberChapters() {
    var sections = doc.querySelectorAll('.instore[data-chapter="true"]');
    var n = 0;
    for (var i = 0; i < sections.length; i++) {
      var out = sections[i].querySelector('.ist-chapter');
      if (!out) continue; /* a section that renders no eyebrow must not consume a number */
      n += 1;
      out.textContent = (n < 10 ? '0' : '') + n;
    }
    var off = doc.querySelectorAll('.instore:not([data-chapter="true"]) .ist-chapter');
    for (var j = 0; j < off.length; j++) off[j].textContent = '';
  }

  /* ---------- Headings: split H1 and H2 into words so each can rise out of its own mask.
       The full sentence stays the heading's accessible name; the word wrappers are presentation only.
       An element inside a heading (the no-wrap "in-store") is kept whole as one word. ---------- */
  function wordWrap(node, index) {
    var outer = doc.createElement('span');
    outer.className = 'ist-word';
    outer.style.setProperty('--ist-word-i', String(index));
    var inner = doc.createElement('span');
    inner.className = 'ist-word__in';
    inner.appendChild(node);
    outer.appendChild(inner);
    return outer;
  }
  function splitHeadings() {
    if (mqReduce.matches) return;
    var heads = doc.querySelectorAll('.instore[data-reveal="true"] .ist-h--1.ist-reveal, .instore[data-reveal="true"] .ist-h--2.ist-reveal, .instore[data-reveal="true"] .instore-craft__statement.ist-reveal');
    for (var i = 0; i < heads.length; i++) {
      var h = heads[i];
      if (h.__istSplit) continue;
      h.__istSplit = true;
      var label = (h.textContent || '').replace(/\s+/g, ' ').replace(/^\s|\s$/g, '');
      if (!label) continue;
      var holder = doc.createElement('span');
      holder.setAttribute('aria-hidden', 'true');
      var nodes = Array.prototype.slice.call(h.childNodes);
      var count = 0;
      for (var k = 0; k < nodes.length; k++) {
        var node = nodes[k];
        if (node.nodeType === 3) {
          var parts = node.nodeValue.split(/(\s+)/);
          for (var p = 0; p < parts.length; p++) {
            if (!parts[p]) continue;
            if (/^\s+$/.test(parts[p])) holder.appendChild(doc.createTextNode(' '));
            else holder.appendChild(wordWrap(doc.createTextNode(parts[p]), count++));
          }
        } else if (node.nodeType === 1) {
          holder.appendChild(wordWrap(node, count++));
        }
      }
      while (h.firstChild) h.removeChild(h.firstChild);
      h.appendChild(holder);
      h.setAttribute('aria-label', label);
      h.classList.add('is-split');
      h.__istWords = count;
    }
  }

  /* ---------- Count-up: a stat figure runs up to its value as it is revealed.
       Thousands separators, decimals, prefixes and suffixes are kept as typed; anything that is not a number is left alone. ---------- */
  function parseFigure(text) {
    var m = /^([^\d]*)(\d[\d\s,. ]*)([\s\S]*)$/.exec(text);
    if (!m) return null;
    var raw = m[2].replace(/[\s ,.]+$/, '');
    var suffix = m[2].slice(raw.length) + m[3];
    var grouped = /^\d{1,3}([,.\s ])\d{3}(?:\1\d{3})*$/.exec(raw);
    if (grouped) return { prefix: m[1], suffix: suffix, value: parseInt(raw.replace(/\D/g, ''), 10), sep: grouped[1], decimals: 0, point: '' };
    if (/^\d+$/.test(raw)) return { prefix: m[1], suffix: suffix, value: parseInt(raw, 10), sep: '', decimals: 0, point: '' };
    var dec = /^(\d+)([.,])(\d{1,2})$/.exec(raw);
    if (dec) return { prefix: m[1], suffix: suffix, value: parseFloat(dec[1] + '.' + dec[3]), sep: '', decimals: dec[3].length, point: dec[2] };
    return null;
  }
  function formatFigure(f, v) {
    var s;
    if (f.decimals) s = v.toFixed(f.decimals).replace('.', f.point);
    else {
      s = String(Math.round(v));
      if (f.sep) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, f.sep);
    }
    return f.prefix + s + f.suffix;
  }
  function countUp(el, delay) {
    if (el.__istCounted) return;
    el.__istCounted = true;
    if (mqReduce.matches) return;
    var finalText = (el.textContent || '').replace(/^\s+|\s+$/g, '');
    var f = parseFigure(finalText);
    if (!f || !f.value) return;
    var width = el.getBoundingClientRect().width;
    if (width) el.style.minWidth = Math.ceil(width) + 'px'; /* neighbours hold still while the digits run */
    var sr = doc.createElement('span');
    sr.className = 'ist-sr';
    sr.textContent = finalText;
    el.parentNode.insertBefore(sr, el);
    el.setAttribute('aria-hidden', 'true');
    el.textContent = formatFigure(f, 0);
    var duration = 2000;
    var start = 0;
    function tick() {
      var t = clamp((now() - start) / duration, 0, 1);
      var eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
      el.textContent = t >= 1 ? finalText : formatFigure(f, f.value * eased);
      if (t < 1) { window.requestAnimationFrame(tick); return; }
      el.removeAttribute('aria-hidden');
      el.style.removeProperty('min-width');
      if (sr.parentNode) sr.parentNode.removeChild(sr);
    }
    window.setTimeout(function () { start = now(); tick(); }, delay || 0);
  }

  /* ---------- Reveal on scroll (per-section toggle via data-reveal).
       A reveal holding a photograph waits for it to decode, briefly, so the unveil shows the picture and not an empty frame. ---------- */
  function whenImagesReady(el, cb) {
    var imgs = el.querySelectorAll('img');
    var pending = 0;
    var done = false;
    function finish() { if (done) return; done = true; cb(); }
    function watch(img) {
      function once() {
        img.removeEventListener('load', once);
        img.removeEventListener('error', once);
        pending -= 1;
        if (pending <= 0) finish();
      }
      img.addEventListener('load', once);
      img.addEventListener('error', once);
    }
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      pending += 1;
      watch(imgs[i]);
    }
    if (!pending) { finish(); return; }
    window.setTimeout(finish, 600); /* a slow photograph holds its own unveil back for a moment, never for long */
  }
  function revealNow(el) {
    el.classList.add('is-in');
    var figures = el.querySelectorAll('.instore-craft__figure');
    if (!figures.length) return;
    var delay = parseInt(el.style.getPropertyValue('--ist-reveal-delay'), 10) || 0;
    for (var i = 0; i < figures.length; i++) countUp(figures[i], delay + 150);
  }
  function reveal(el) {
    if (el.__istRevealing) return;
    el.__istRevealing = true;
    whenImagesReady(el, function () { revealNow(el); });
  }
  /* Pacing: members of one group that arrive in the same frame follow one another (80ms apart, photographs 60ms),
     and whatever follows a split heading waits for its words to land. It is worked out among the arrivals only,
     so an element that scrolls in on its own, or the second row of a grid, starts at once. */
  function isPhotograph(el) {
    var c = el.classList;
    return c.contains('ist-media') || c.contains('ist-split__media') || c.contains('instore-visit__media-col');
  }
  function inDocumentOrder(a, b) {
    return (a.compareDocumentPosition(b) & 4) ? -1 : 1; /* 4: b follows a */
  }
  function pace(arrivals) {
    var groups = [];
    var lists = [];
    var i;
    for (i = 0; i < arrivals.length; i++) {
      var el = arrivals[i];
      var group = el.closest('.ist-reveal-group');
      if (!group) { el.style.setProperty('--ist-reveal-delay', '0ms'); continue; }
      var at = groups.indexOf(group);
      if (at < 0) { groups.push(group); lists.push([]); at = groups.length - 1; }
      lists[at].push(el);
    }
    for (i = 0; i < lists.length; i++) {
      var list = lists[i].sort(inDocumentOrder);
      var delay = 0;
      for (var k = 0; k < list.length; k++) {
        list[k].style.setProperty('--ist-reveal-delay', Math.min(delay, 900) + 'ms');
        delay += isPhotograph(list[k]) ? 60 : 80;
        if (list[k].__istSplit) delay += Math.min(list[k].__istWords * 45, 270);
      }
    }
  }
  function arrive(observer, entries) {
    var arrivals = [];
    for (var k = 0; k < entries.length; k++) {
      if (!entries[k].isIntersecting) continue;
      observer.unobserve(entries[k].target);
      arrivals.push(entries[k].target);
    }
    if (!arrivals.length) return;
    pace(arrivals);
    for (var i = 0; i < arrivals.length; i++) reveal(arrivals[i]);
  }
  var io = null;
  var ioPhoto = null;
  function setupReveals() {
    var items = doc.querySelectorAll('.instore[data-reveal="true"] .ist-reveal:not(.is-in)');
    if (!items.length) return;
    if (mqReduce.matches || !('IntersectionObserver' in window)) {
      for (var i = 0; i < items.length; i++) items[i].classList.add('is-in');
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) { arrive(io, entries); }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      /* photographs start as soon as their top edge is on the screen: they are unveiled from the top, so nothing is missed */
      ioPhoto = new IntersectionObserver(function (entries) { arrive(ioPhoto, entries); }, { rootMargin: '0px 0px -2% 0px', threshold: 0.01 });
    }
    for (var j = 0; j < items.length; j++) {
      var el = items[j];
      if (el.__istRevealing || el.__istWatched) continue;
      el.__istWatched = true;
      (isPhotograph(el) ? ioPhoto : io).observe(el);
    }
  }

  /* ---------- Hero opening: the photograph comes up out of black once it has decoded ---------- */
  function readyHeroes() {
    var heroes = doc.querySelectorAll('.instore-hero');
    for (var i = 0; i < heroes.length; i++) bindHero(heroes[i]);
  }
  function bindHero(hero) {
    if (hero.__istReady) return;
    hero.__istReady = true;
    var img = hero.querySelector('.instore-hero__media img');
    var done = false;
    function ready() {
      if (done) return;
      done = true;
      /* two frames, so the dark opening state is painted before it is released */
      window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { hero.classList.add('is-ready'); }); });
    }
    if (!img || img.complete) { ready(); return; }
    img.addEventListener('load', ready);
    img.addEventListener('error', ready);
    window.setTimeout(ready, 2500);
  }

  /* ---------- Scroll-linked pass: hero parallax, photograph drift, page progress.
       One animation frame per scroll event at most. Pointer-driven effects never run on touch. ---------- */
  var progressEl = null;
  function setupProgress() {
    var wanted = doc.querySelector('.instore-hero[data-progress="true"]');
    if (!wanted) {
      if (progressEl && progressEl.parentNode) progressEl.parentNode.removeChild(progressEl);
      progressEl = null;
      return;
    }
    if (progressEl) return;
    progressEl = doc.createElement('div');
    progressEl.className = 'instore-progress';
    progressEl.setAttribute('aria-hidden', 'true');
    progressEl.appendChild(doc.createElement('span'));
    doc.body.appendChild(progressEl);
  }
  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    var still = mqReduce.matches || mqCoarse.matches;
    var i;

    var heroes = doc.querySelectorAll('.instore-hero[data-parallax="true"]');
    for (i = 0; i < heroes.length; i++) {
      var img = heroes[i].querySelector('.instore-hero__media img');
      if (!img) continue;
      if (still) { img.style.removeProperty('--ist-parallax-y'); continue; }
      var hr = heroes[i].getBoundingClientRect();
      if (hr.bottom < 0 || hr.top > vh) continue;
      img.style.setProperty('--ist-parallax-y', Math.round(Math.max(-hr.top, 0) * 0.22) + 'px');
    }

    var frames = doc.querySelectorAll('.instore[data-reveal="true"] .ist-media--ratio');
    for (i = 0; i < frames.length; i++) {
      if (still) { frames[i].style.removeProperty('--ist-drift'); continue; }
      var fr = frames[i].getBoundingClientRect();
      if (!fr.height || fr.bottom < -80 || fr.top > vh + 80) continue;
      /* -1 as the frame enters at the bottom of the screen, 0 at the centre, 1 as it leaves at the top */
      var pos = (vh / 2 - (fr.top + fr.height / 2)) / ((vh + fr.height) / 2);
      frames[i].style.setProperty('--ist-drift', clamp(pos, -1, 1).toFixed(3));
    }

    if (progressEl) {
      var sections = doc.querySelectorAll('.instore');
      if (sections.length) {
        var y = pageY();
        var first = sections[0].getBoundingClientRect().top + y;
        var last = sections[sections.length - 1].getBoundingClientRect().bottom + y - vh;
        var p = last > first ? clamp((y - first) / (last - first), 0, 1) : 0;
        progressEl.style.setProperty('--ist-progress', p.toFixed(4));
        if (y > first + 60 && y < last + vh * 0.5) progressEl.classList.add('is-on');
        else progressEl.classList.remove('is-on');
      }
    }
  }
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  /* ---------- In-page links (Come visit, Tour inquiries): glide to the section, clear of the sticky header,
       then hand focus to it so keyboard and screen reader users arrive too ---------- */
  function onClick(e) {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!link || !link.closest('.instore')) return;
    var id = (link.getAttribute('href') || '').slice(1);
    if (!id) return;
    var target = doc.getElementById(id);
    if (!target) return;
    e.preventDefault();
    measureHeader();
    target.scrollIntoView({ behavior: mqReduce.matches ? 'auto' : 'smooth', block: 'start' });
    if (window.history && window.history.pushState) window.history.pushState(null, '', '#' + id);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (err) { /* older browsers: focus would jump the page, so leave it */ }
  }

  /* ---------- Contact forms: Shopify flags every contact form on the page after a post.
       Narrow the visible success/error state to the form named in the URL hash when we can. ---------- */
  function narrowFormStates() {
    var hash = (window.location.hash || '').replace('#', '');
    var posted = /contact_posted=true/.test(window.location.search);
    var panels = doc.querySelectorAll('.instore .ist-form-panel[data-form-id]');
    if (!panels.length || !hash) return; /* no hash to key on: leave the Liquid output as rendered */
    var target = doc.getElementById(hash);
    if (!target) return;
    for (var i = 0; i < panels.length; i++) {
      var p = panels[i];
      var mine = p.getAttribute('data-form-id') === hash || p.contains(target);
      var states = p.querySelectorAll('.ist-form__status');
      for (var k = 0; k < states.length; k++) {
        if (!mine) states[k].hidden = true;
      }
      if (mine && posted) {
        var fields = p.querySelector('.ist-form__fields');
        if (fields) fields.hidden = true;
      }
    }
  }

  /* ---------- Boot / rescan ---------- */
  function measure() {
    measureHeader();
    measureHeroes();
  }
  function scan() {
    measure();
    numberChapters();
    splitHeadings();
    setupReveals();
    readyHeroes();
    setupProgress();
    frame();
  }
  function rescanSoon() { window.setTimeout(scan, 30); }
  function boot() {
    scan();
    narrowFormStates();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); onScroll(); });
    window.addEventListener('load', function () { measure(); onScroll(); });
    doc.addEventListener('click', onClick);
    doc.addEventListener('shopify:section:load', rescanSoon);
    doc.addEventListener('shopify:section:select', rescanSoon);
    doc.addEventListener('shopify:section:unload', rescanSoon);
    doc.addEventListener('shopify:section:reorder', rescanSoon);
    doc.addEventListener('shopify:block:select', rescanSoon);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
})();
